import { secondaryStreamingApiConnector } from 'apiConnector/secondary-market/streaming';
import { ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  secondaryMarketPriceCleared,
  secondaryMarketPriceSubscribed,
  secondaryMarketPriceSubscribeFailed,
  secondaryMarketPriceUnsubscribeFailed,
} from 'state/secondaryMarket/realtime/realtime.action';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import type { EpicWithThunk } from 'state/state.common';

import { waitUntilConnectedToSignalR } from '../utils';

export function subscribeDlpLiveStreamingEpic(): EpicWithThunk {
  return (action$, _state$, { httpClient }) => {
    const addTab$ = action$.pipe(
      ofType('SECONDARY_MARKET_QUOTES_WORKSPACE_ADD_TAB'),
      filter(tab => tab.tabType === 'dlpProducts'),
    );

    const { subscribe } = secondaryStreamingApiConnector(httpClient);
    return waitUntilConnectedToSignalR(addTab$, action$).pipe(
      mergeMap(({ key }) => {
        const productKey = toBcsProductKey(key);
        if (productKey === undefined) {
          return EMPTY;
        }
        return subscribe({ productIds: [productKey], backend: 'Dlp' }).pipe(
          map(() => secondaryMarketPriceSubscribed(productKey)),
          catchError(() => of(secondaryMarketPriceSubscribeFailed(productKey))),
        );
      }),
    );
  };
}

export function unsubscribeDlpLiveStreamingEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    {
      httpClient,
      selectors: {
        fromSecondaryQuotesWorkspace: { hasOtherTabsAssociatedToSameProductKey },
      },
    },
  ) => {
    const { unsubscribe } = secondaryStreamingApiConnector(httpClient);

    return action$.pipe(
      ofType('SECONDARY_MARKET_QUOTES_WORKSPACE_REMOVE_TAB'),
      filter(tab => tab.tabType === 'dlpProducts'),
      withLatestFrom(state$),
      mergeMap(([{ productKey, tabId }, state]) => {
        if (hasOtherTabsAssociatedToSameProductKey(state, productKey, tabId)) {
          return EMPTY; // can not unsubsribce yet
        }

        const bcsProductKey = toBcsProductKey(productKey)!;
        return unsubscribe({ productIds: [bcsProductKey], backend: 'Dlp' }).pipe(
          map(() => secondaryMarketPriceCleared(bcsProductKey)),
          catchError(() => of(secondaryMarketPriceUnsubscribeFailed(bcsProductKey))),
        );
      }),
    );
  };
}
